import { useCallback, useMemo } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

import { CONFIG } from '@frontend/config';
import { forgetRoute } from '@frontend/features/common/hooks/useLastVisitedUrl';

import {
  addRecent,
  loadRecents,
  persistRecents,
  todayISO,
} from './recentSearches';
import { BOOKING_CASE_PARAM } from './bookingCaseContext';

/**
 * La BÚSQUEDA vive en la URL; los RESULTADOS no.
 *
 * Por qué: un buscador cuyo estado muere al navegar no se puede compartir, no
 * se puede volver a él con el botón "atrás" y pierde todo al cambiar de
 * vertical. Poniendo los parámetros en la URL, cada búsqueda es un link.
 *
 * Los resultados NO se serializan a propósito: React Query decide si al montar
 * esos parámetros muestra una generación cacheada y refresca, o ejecuta una
 * búsqueda en vivo. La vigencia reservable de cualquier generación visible se
 * decide por su propio `dataUpdatedAt`, no por la edad de la URL recordada.
 *
 * Tampoco viaja NADA personal: parámetros de viaje sí (fechas, IATA, cantidad
 * de pasajeros, edades), datos de los pasajeros de una reserva jamás.
 */

/**
 * Marca de "esta búsqueda vino de una reciente". No se cotiza con ella: existe
 * para que cambie la CLAVE de montaje del formulario (ver `useSearchFormKey`).
 * Se queda en la URL mientras dure la búsqueda, así refinar no remonta nada.
 */
export const RECENT_PARAM = 'r';

/** Un valor de formulario tal como se serializa: strings o nada. */
export type UrlParams = Record<string, string | undefined>;

/** Params ya normalizados: sin vacíos, listos para comparar y cachear. */
export function cleanParams(params: UrlParams): Record<string, string> {
  return Object.fromEntries(
    Object.entries(params)
      .filter(([, value]) => value !== undefined && value !== '')
      .map(([key, value]) => [key, String(value)]),
  );
}

export interface SearchUrlState {
  /** Lo que hay HOY en la URL (fuente de verdad del formulario y del search). */
  params: Record<string, string>;
  /**
   * ¿Hay algo que limpiar? Sirve para no dibujar un botón "Limpiar" que no
   * limpiaría nada, en un formulario que el asesor todavía no tocó.
   */
  hasParams: boolean;
  /**
   * Escribe el formulario en la URL SIN crear entrada de historial: tipear no
   * tiene que llenar el historial de pasos intermedios.
   */
  update: (partial: UrlParams) => void;
  /**
   * Confirma una búsqueda: crea entrada de historial, así "atrás" vuelve a la
   * búsqueda anterior — que es lo que el usuario espera.
   */
  commit: (partial?: UrlParams) => void;
  /**
   * Limpia todo (cambio de contexto, no un refinamiento) y OLVIDA la ruta:
   * vaciar el formulario sin olvidar la memoria de navegación sería mentir —
   * el agente se va a otro vertical, vuelve, y reaparece lo que limpió.
   */
  reset: () => void;
}

export interface SearchUrlOptions {
  /**
   * A qué vertical pertenece la búsqueda. Con esto, `commit` la guarda en
   * recientes: una sola llamada en vez de repetirlo en los ocho buscadores.
   */
  vertical?: string;
  /**
   * Qué parámetro lleva la fecha de viaje (`YYYY-MM-DD`), para que la reciente
   * caduque sola cuando el viaje ya salió. eSIMs y pases no tienen.
   */
  dateParam?: string;
}

export function useSearchUrlState(
  options: SearchUrlOptions = {},
): SearchUrlState {
  const [searchParams, setSearchParams] = useSearchParams();
  const { pathname } = useLocation();
  const { vertical, dateParam } = options;

  const params = useMemo(
    () => Object.fromEntries(searchParams.entries()),
    [searchParams],
  );

  const write = useCallback(
    (partial: UrlParams, replace: boolean) => {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          for (const [key, value] of Object.entries(partial)) {
            if (value === undefined || value === '') next.delete(key);
            else next.set(key, String(value));
          }
          return next;
        },
        { replace },
      );
    },
    [setSearchParams],
  );

  return {
    params,
    hasParams: Object.keys(params).some((key) => key !== BOOKING_CASE_PARAM),
    update: useCallback((partial: UrlParams) => write(partial, true), [write]),
    commit: useCallback(
      (partial: UrlParams = {}) => {
        write(partial, false);
        // Se guarda la INTENCIÓN, no el resultado: los parámetros no vencen,
        // los precios sí. Solo búsquedas ejecutables — un formulario a medias
        // no es una reciente.
        const merged = cleanParams({ ...params, ...partial });
        if (!vertical || merged.buscar !== '1') return;
        // La marca de remontaje no es parte de la búsqueda: si se guardara,
        // la misma búsqueda aplicada dos veces contaría como dos distintas.
        delete merged[RECENT_PARAM];
        // El caso es contexto comercial, no parte de una búsqueda reutilizable.
        // Guardarlo en recientes reabriría otro viaje meses después.
        delete merged[BOOKING_CASE_PARAM];
        const today = todayISO();
        persistRecents(
          CONFIG.AGENCY_ID,
          addRecent(
            loadRecents(CONFIG.AGENCY_ID),
            {
              vertical,
              params: merged,
              ...(dateParam && merged[dateParam]
                ? { travelDate: merged[dateParam] }
                : {}),
              savedAt: Date.now(),
            },
            today,
          ),
        );
      },
      [write, params, vertical, dateParam],
    ),
    reset: useCallback(() => {
      setSearchParams((current) => {
        const next = new URLSearchParams();
        const caseId = current.get(BOOKING_CASE_PARAM);
        if (caseId) next.set(BOOKING_CASE_PARAM, caseId);
        return next;
      }, { replace: true });
      forgetRoute(pathname);
    }, [setSearchParams, pathname]),
  };
}

/**
 * Clave de montaje del buscador. Los campos se inicializan AL MONTAR desde la
 * URL, así que una búsqueda que llega de afuera (tocar una reciente) cambiaría
 * los resultados dejando el formulario vacío: se vería una lista de Ámsterdam →
 * París sobre un formulario en blanco. Cambiando la clave, React remonta el
 * buscador y este vuelve a leer la URL.
 *
 * Solo cambia al aplicar una reciente: escribir, refinar o buscar conservan el
 * parámetro, y por lo tanto la clave — remontar mientras se escribe borraría lo
 * tipeado.
 */
export function useSearchFormKey(): string {
  const [searchParams] = useSearchParams();
  return searchParams.get(RECENT_PARAM) ?? '';
}

/** Entero de la URL con default (la URL siempre puede venir a mano). */
export function numParam(
  params: Record<string, string>,
  key: string,
  fallback: number,
  { min = 0, max = Number.MAX_SAFE_INTEGER } = {},
): number {
  const parsed = Number(params[key]);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.min(max, Math.max(min, Math.trunc(parsed)));
}

/** Valor de la URL acotado a una lista de opciones válidas. */
export function enumParam<T extends string>(
  params: Record<string, string>,
  key: string,
  allowed: readonly T[],
  fallback: T,
): T {
  const value = params[key] as T | undefined;
  return value && allowed.includes(value) ? value : fallback;
}

/** Lista separada por comas ("MINIBUS,SHUTTLE"), sin vacíos. */
export function listParam(
  params: Record<string, string>,
  key: string,
): string[] {
  return (params[key] ?? '')
    .split(',')
    .map((value) => value.trim())
    .filter(Boolean);
}

export function serializeList(values: string[]): string | undefined {
  return values.length ? values.join(',') : undefined;
}

/**
 * ¿La URL PIDE una búsqueda que no se puede ejecutar? Pasa con un link editado
 * a mano o recortado al copiarlo: sin esto la pantalla se queda muda y parece
 * que el deep link no funciona.
 */
export function isIncompleteSearch(
  params: Record<string, string>,
  payload: unknown,
): boolean {
  return params.buscar === '1' && payload === null;
}
